import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Tree, Button, Modal, Form, Input, Select, Spin, Tooltip, Skeleton, Switch } from 'antd'
import { PlusOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons'
import { getMenuData, getRolesData, saveMenuData } from '../../../store/menu_editor/action-creators'
import B2BLayout from '../../../layouts/b2b'
import { MapContent, MapTitle, MapWrapper, TaskWrapper } from '../../users/card/styles'
import IconSelect from '../../../components/icon-selector'
import IconComponent from '../../../components/icon-component'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import Result from 'antd/lib/result'
import {
  getMenuLinkByUrl,
  getMenuLinksCatalog,
  isAllowedMenuUrl,
  normalizeMenuUrl,
} from '../../../menu/linksCatalog'

const { TreeNode } = Tree
const { Option } = Select

interface MenuItem {
  id: number
  title?: string
  url?: string
  roles?: number[]
  children?: MenuItem[]
  parent?: number
  icon?: string
}

type FormValues = {
  title?: string
  url?: string
  roles?: number[]
  icon?: string
  isGroup?: boolean
}

const MenuEditor: React.FC = () => {
  const dispatch = useAppDispatch()
  const { data, isLoading, error, roles, rolesLoading } = useSelector(
    (state: any) => state.menuEditor,
  )
  const allowedMenuKeys = useSelector((state: any) => state?.session?.sessionData?.allowedMenuKeys)
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string>('Свойства')
  const [currentItem, setCurrentItem] = useState<MenuItem | null>(null)
  const [form] = Form.useForm()
  const [filteredRoles, setFilteredRoles] = useState(roles)
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])

  useEffect(() => {
    if (roles.length) {
      setFilteredRoles(roles)
    }
  }, [roles])

  useEffect(() => {
    dispatch(getMenuData())
    dispatch(getRolesData())
  }, [dispatch])

  const onAdd = (parentKey: number) => {
    form.setFieldsValue({
      parent: parentKey,
      title: undefined,
      url: undefined,
      roles: [],
      icon: undefined,
      isGroup: false,
    })
    const item = {
      id: 0,
      parent: parentKey,
      title: undefined,
      url: undefined,
      roles: [],
      icon: undefined,
    }
    setCurrentItem(item)
    setModalTitle('Добавление нового элемента')
    setModalVisible(true)
  }

  const onAddRoot = () => onAdd(0)

  const onEdit = useCallback(
    (item: MenuItem) => {
      setCurrentItem(item)
      const isGroup = !item.url
      form.setFieldsValue({
        title: item.title,
        url: item.url,
        roles: item.roles,
        icon: item.icon,
        isGroup,
      })
      setModalTitle('Свойства')
      setModalVisible(true)
    },
    [setCurrentItem, currentItem],
  )

  const handleModalOk = useCallback(async () => {
    try {
      const values = (await form.validateFields()) as FormValues
      const isGroup = !!values.isGroup
      const normalizedUrl = isGroup ? '' : normalizeMenuUrl(values.url)
      const link = isGroup ? undefined : getMenuLinkByUrl(normalizedUrl)

      const updateItem = (items: MenuItem[]): MenuItem[] => {
        // Добавление нового корневого пункта
        if (currentItem?.id === 0 && currentItem?.parent === 0) {
          const nextItem: MenuItem = {
            ...(currentItem as MenuItem),
            ...values,
            url: normalizedUrl,
            title: values.title ?? link?.title,
            icon: values.icon ?? link?.defaultIcon,
          }
          return [...items, nextItem]
        }

        return items.map(item => {
          if (currentItem?.id === 0 && currentItem?.parent === item?.id) {
            if (item?.children?.length) {
              return {
                ...item,
                children: [
                  ...item.children,
                  {
                    ...(currentItem as MenuItem),
                    ...values,
                    url: normalizedUrl,
                    title: values.title ?? link?.title,
                    icon: values.icon ?? link?.defaultIcon,
                  },
                ],
              }
            }
            return {
              ...item,
              children: [
                {
                  ...(currentItem as MenuItem),
                  ...values,
                  url: normalizedUrl,
                  title: values.title ?? link?.title,
                  icon: values.icon ?? link?.defaultIcon,
                },
              ],
            }
          }
          if (item.id === currentItem?.id) {
            return {
              ...item,
              ...values,
              url: normalizedUrl,
              title: values.title ?? link?.title ?? item.title,
              icon: values.icon ?? link?.defaultIcon ?? item.icon,
            }
          }
          if (item.children) {
            return { ...item, children: updateItem(item.children) }
          }
          return item
        })
      }
      const updatedData = updateItem(data)
      dispatch(saveMenuData(updatedData))
      setModalVisible(false)
      setCurrentItem(null)
    } catch (error) {
      console.error('Ошибка сохранения данных:', error)
    }
  }, [form, currentItem])

  const handleModalCancel = () => {
    setModalVisible(false)
    setCurrentItem(null)
  }

  const renderTreeNodes = (items: MenuItem[]): React.ReactNode => {
    return items.map(item => {
      if (item.children) {
        return (
          <TreeNode title={renderTitle(item)} key={item.id}>
            {renderTreeNodes(item.children)}
          </TreeNode>
        )
      }
      return <TreeNode key={item.id} title={renderTitle(item)} />
    })
  }

  const renderTitle = (item: MenuItem) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {item?.icon && (
        <span style={{ marginRight: 10 }}>
          <IconComponent iconName={item.icon} />
        </span>
      )}
      <span>{item.title}</span>
      <div style={{ marginLeft: 15 }}>
        <Button
          icon={<PlusOutlined />}
          size="small"
          type="link"
          onClick={() => onAdd(item.id)}
          style={{ marginRight: 4 }}
        />
        <Button
          icon={<EditOutlined />}
          size="small"
          type="link"
          onClick={() => onEdit(item)}
          style={{ marginRight: 4 }}
        />
        {!!item.url && !isAllowedMenuUrl(item.url, allowedMenuKeys) && (
          <Tooltip
            title="Ссылка не из каталога. Откройте свойства пункта и выберите ссылку из списка."
            placement={'right'}
            color={'#F81D22'}
            style={{ maxWidth: 'max-content' }}
          >
            <WarningOutlined style={{ color: '#F81D22', marginLeft: '5px' }} />
          </Tooltip>
        )}
        {!item.roles?.length && (
          <Tooltip
            title="Пункт меню будет скрыт для всех, т.к. не выбраны роли!"
            placement={'right'}
            color={'#F81D22'}
            style={{ maxWidth: 'max-content' }}
          >
            <WarningOutlined style={{ color: '#F81D22', marginLeft: '5px' }} />
          </Tooltip>
        )}
      </div>
    </div>
  )

  const breadcrumbs = [
    {
      url: '/admin',
      title: 'Admin',
    },
  ]

  const onDrop = (info: any) => {
    const dropKey = info.node.key
    const dragKey = info.dragNode.key
    const dropPos = info.node.pos.split('-')
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1])

    const loop = (
      data: MenuItem[],
      id: number,
      callback: (item: MenuItem, index: number, arr: MenuItem[]) => void,
    ) => {
      for (let i = 0; i < data.length; i++) {
        if (+data[i].id === +id) {
          return callback(data[i], i, data)
        }
        if (data[i].children) {
          loop(data[i].children!, +id, callback)
        }
      }
    }

    const dataCopy = [...data]

    let dragObj: MenuItem
    loop(dataCopy, +dragKey, (item, index, arr) => {
      arr.splice(index, 1)
      dragObj = item
    })

    if (!info.dropToGap) {
      loop(dataCopy, +dropKey, item => {
        item.children = item.children || []
        item.children.push(dragObj!)
      })
    } else if ((info.node.children || []).length > 0 && info.node.expanded && dropPosition === 1) {
      loop(dataCopy, +dropKey, item => {
        item.children = item.children || []
        item.children.unshift(dragObj!)
      })
    } else {
      let ar: MenuItem[] = []
      let i: number
      loop(dataCopy, +dropKey, (_item, index, arr) => {
        ar = arr
        i = index
      })
      if (dropPosition === -1) {
        ar.splice(i!, 0, dragObj!)
      } else {
        ar.splice(i! + 1, 0, dragObj!)
      }
    }

    dispatch(saveMenuData(dataCopy))
  }
  /*
  const icons = [
    'BarChartOutlined',
    'BankOutlined',
    'StarOutlined',
    'SettingOutlined',
    'HomeOutlined',
    'HeartOutlined',
    'DollarOutlined',
    'UserOutlined',
    'PhoneOutlined',
    'CarOutlined',
    'AreaChartOutlined',
    'PieChartOutlined',
    'LineChartOutlined',
    'FormatPainterOutlined',
    'GlobalOutlined',
    'HistoryOutlined',
    'ProductOutlined',
    'FileExclamationOutlined',
    'ToolOutlined',
    'ColumnWidthOutlined',
    'HammerOutlined',
    'IconCoding',
    'IconPowerBI',
    'TeamOutlined',
    'UsergroupAddOutlined',
    'ContainerOutlined',
    'TagsOutlined',
    'BgColorsOutlined',
  ]
  */
  const icons = useMemo(() => {
    const antIcons = Object.keys(require('@ant-design/icons')).filter(k => k.endsWith('Outlined'))
    const customIcons = ['HammerOutlined', 'IconCoding', 'IconPowerBI']
    return [...antIcons, ...customIcons]
  }, [])

  const handleSearch = val => {
    const filtered = roles.filter(role => role.name.toLowerCase().includes(val.toLowerCase()))
    setFilteredRoles(filtered)
  }

  const catalog = useMemo(() => getMenuLinksCatalog(allowedMenuKeys), [allowedMenuKeys])
  const catalogByGroup = useMemo(() => {
    return catalog.reduce((acc: Record<string, typeof catalog>, link) => {
      if (!acc[link.group]) acc[link.group] = []
      acc[link.group].push(link)
      return acc
    }, {})
  }, [catalog])

  const handleLinkChange = (nextUrl: string) => {
    const normalized = normalizeMenuUrl(nextUrl)
    const link = getMenuLinkByUrl(normalized)
    const currentTitle = form.getFieldValue('title')
    const currentIcon = form.getFieldValue('icon')

    form.setFieldsValue({
      url: normalized,
      title: currentTitle ?? link?.title,
      icon: currentIcon ?? link?.defaultIcon,
    })
  }

  const handleGroupToggle = (checked: boolean) => {
    if (checked) {
      form.setFieldsValue({ url: '' })
    }
  }

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {error && (
        <Result status={error.statusCode || 'error'} title="Ошибка" subTitle={error.message} />
      )}
      {!error && (
        <>
          <MapWrapper>
            <TaskWrapper>
              <MapTitle>Редактор меню</MapTitle>
              <Button type="primary" onClick={onAddRoot}>
                Добавить корневой пункт
              </Button>
            </TaskWrapper>
            <MapContent>
              {(isLoading || rolesLoading) && <Spin />}
              <Tree
                draggable
                onDrop={onDrop}
                expandedKeys={expandedKeys}
                onExpand={keys => setExpandedKeys(keys)}
              >
                {renderTreeNodes(data)}
              </Tree>
              <Modal
                title={modalTitle}
                open={modalVisible}
                maskClosable={true}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
              >
                <Form form={form} layout="vertical">
                  <Form.Item label="Иконка" name="icon">
                    <IconSelect iconList={icons} />
                  </Form.Item>
                  <Form.Item
                    name="title"
                    label="Название"
                    rules={[{ required: true, message: 'Укажите название!' }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="isGroup"
                    label="Группа (без ссылки)"
                    valuePropName="checked"
                    tooltip="Для родительских категорий. Ссылка не требуется, пункт будет раскрывать вложенные элементы."
                  >
                    <Switch onChange={handleGroupToggle} />
                  </Form.Item>
                  <Form.Item
                    name="url"
                    label="Ссылка"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator: async (_, value) => {
                          const isGroup = !!getFieldValue('isGroup')
                          if (isGroup) return

                          if (!value) {
                            throw new Error('Выберите ссылку из каталога')
                          }
                          const normalized = normalizeMenuUrl(value)
                          if (!isAllowedMenuUrl(normalized, allowedMenuKeys)) {
                            throw new Error('Ссылка должна быть выбрана из каталога')
                          }
                        },
                      }),
                      {
                        required: false,
                      },
                    ]}
                  >
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.isGroup !== next.isGroup}>
                      {({ getFieldValue }) => {
                        const isGroup = !!getFieldValue('isGroup')
                        return (
                          <Select
                            showSearch
                            placeholder={
                              isGroup ? 'Для группы ссылка не требуется' : 'Выберите раздел'
                            }
                            onChange={handleLinkChange}
                            disabled={isGroup}
                            allowClear={!isGroup}
                            filterOption={(input, option) =>
                              String(option?.children ?? '')
                                .toLowerCase()
                                .includes(String(input ?? '').toLowerCase())
                            }
                          >
                            {Object.entries(catalogByGroup).map(([group, links]) => (
                              <Select.OptGroup key={group} label={group}>
                                {links.map(link => (
                                  <Option key={link.url} value={link.url}>
                                    {link.title} ({link.url})
                                  </Option>
                                ))}
                              </Select.OptGroup>
                            ))}
                          </Select>
                        )
                      }}
                    </Form.Item>
                  </Form.Item>
                  <Form.Item name="roles" label="Роли">
                    {filteredRoles.length && (
                      <Select mode="multiple" onSearch={handleSearch} filterOption={false}>
                        {filteredRoles.map(role => (
                          <Option key={role.id} value={role.id}>
                            {role.name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Form>
              </Modal>
            </MapContent>
          </MapWrapper>
        </>
      )}
    </B2BLayout>
  )
}

export default MenuEditor
